import { ResourceType } from '@server/collections/fileSystem/types/fileSystem.types';
import { Injectable } from '@angular/core';
import { Cloudinary } from '@cloudinary/url-gen/instance/Cloudinary';
import { crop, fill, scale, pad, thumbnail } from '@cloudinary/url-gen/actions/resize';
import { focusOn } from "@cloudinary/url-gen/qualifiers/gravity";
import { FocusOn } from "@cloudinary/url-gen/qualifiers/focusOn";
import { byRadius, max } from "@cloudinary/url-gen/actions/roundCorners";
import { CloudinaryImage } from '@cloudinary/url-gen/assets/CloudinaryImage';

export type ResizeMode = 'fill' | 'scale' | 'crop' | 'thumb' | 'avatar' | 'pad';
export enum ResizeModeEnum { 'fill', 'scale', 'crop', 'thumb', 'avatar', 'pad' };
export type UploadPreset = 'image' | 'video' | 'raw' | 'company-video' | 'company-video-4-3';

@Injectable({
  providedIn: 'root'
})
export class CloudinaryService {
  public cloudName = 'expometals';
  public uploadImagePreset = 'upload-client-image';
  public api_key = '554728287486758';
  public uploadPresets = new Map<UploadPreset, string>([
    ['image', 'upload-client-image'],
    ['company-video', '16-9-video'],
    ['company-video-4-3', '4-3-video'],
    ['video', 'video'],
    ['raw', 'raw-files'],
  ])
  constructor() { }

  public get uploadUrl(): string {
    return `https://api.cloudinary.com/v1_1/${this.cloudName}/upload`;
  }

  public get uploadLargeUrl(): string {
    return `${this.uploadUrl}_large`;
  }

  private get cld() {
    return new Cloudinary({
      cloud: {
        cloudName: this.cloudName
      }
    });
  }

  getImageUrl(file: string, w: number, h: number, mode: ResizeMode | ResizeModeEnum, round = false): string {
    const { cld } = this;
    const img = cld.image(file).format('auto').quality('auto');

    return this.transform(img, w, h, mode, round).toURL();
  }

  getVideoImageUrl(public_id: string, w: number, h: number, mode: ResizeMode | ResizeModeEnum) {
    const { cld } = this;
    const img = cld.image(public_id).setAssetType('video').format('auto').quality('auto');
    return this.transform(img, w, h, mode).toURL();
  }

  getDownloadUrl(): string {
    return
  }

  getTruncatedUrl(secureUrl: string): string {
    return secureUrl.match(/v[0-9]{10}.*$/)[0];
  }

  getUrl(public_id: string, resourceType: ResourceType): string {
    return this.cld.image(public_id).setAssetType(resourceType).toURL();
  }

  private transform(img, w: number, h: number, mode: ResizeMode | ResizeModeEnum, round = false): CloudinaryImage {
    switch (mode) {
      case 'fill':
      case ResizeModeEnum.fill:
        img.resize(fill(w, h));
        break;
      case 'scale':
      case ResizeModeEnum.scale:
        img.resize(scale(w, h));
        break;
      case 'crop':
      case ResizeModeEnum.crop:
        img.resize(crop(w, h));
        break;
      case 'avatar':
      case ResizeModeEnum.avatar:
        img.resize(thumbnail(w, h).gravity(focusOn(FocusOn.face()))).roundCorners(max());
        break;
      case 'pad':
      case ResizeModeEnum.pad:
        img.resize(pad(w, h));
        break;
    }

    if (round) {
      img.roundCorners(max());
    }
    return img;
  }

}
